export interface FieldHelpContent {
  caveat?: string;
  example?: string;
  range?: string;
  source?: string;
  units?: string;
  validation?: string;
  whatToEnter: string;
  whyItMatters: string;
}

export interface RegisteredFieldHelp<Key extends string = string>
  extends FieldHelpContent {
  fieldKey: Key;
}

export type RegisteredRegistry = Readonly<
  Record<string, RegisteredFieldHelp>
>;

export function defineFieldHelpRegistry<
  const Scope extends string,
  const Entries extends Readonly<Record<string, FieldHelpContent>>,
>(scope: Scope, entries: Entries) {
  return Object.fromEntries(
    Object.entries(entries).map(([name, content]) => [
      name,
      Object.freeze({ ...content, fieldKey: `${scope}.${name}` }),
    ]),
  ) as {
    readonly [Name in keyof Entries]: RegisteredFieldHelp<
      `${Scope}.${Extract<Name, string>}`
    >;
  };
}

export function mergeFieldHelpRegistries(
  ...registries: readonly RegisteredRegistry[]
): RegisteredRegistry {
  const merged: Record<string, RegisteredFieldHelp> = {};
  for (const registry of registries) {
    for (const field of Object.values(registry)) {
      if (merged[field.fieldKey] !== undefined) {
        throw new Error(`Duplicate Guided field key: ${field.fieldKey}`);
      }
      merged[field.fieldKey] = field;
    }
  }
  return Object.freeze(merged);
}

export function guided(
  field: RegisteredFieldHelp,
  instanceId?: string | number | null,
) {
  return {
    'data-guided-field': field.fieldKey,
    ...(instanceId === undefined || instanceId === null
      ? {}
      : { 'data-guided-instance': String(instanceId) }),
  };
}
